'use client';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import { Clients } from '@susu/custom-made/utils/constants';
import { PANEL_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { useEffect, useState } from 'react';

import './panel.css';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';

export enum PanelSize {
  Full = 'fullSize',
  Content = 'contentSize',
  Multi = 'multiSize',
}

export enum PanelType {
  Regular = 'regular',
  MiddleScreen = 'middleScreen',
}

export enum PanelPosition {
  Regular = 'regular',
  Side = 'side',
}

const getOverlayClasses = (isActive: boolean, withOverlay?: boolean) =>
  cn(
    'fixed top-[var(--webstore-header-height)] bottom-0 left-0 right-0 bg-gray-800/30',
    isActive ? 'opacity-100' : 'opacity-0',
    withOverlay ? 'lg:block' : 'lg:hidden',
  );

const getPanelTopClasses = (isActive: boolean, isContent: boolean, isFull: boolean) => {
  if (!isActive) {
    return 'top-[100%]';
  }
  if (isContent) {
    return 'top-[unset]';
  }

  return isFull ? 'top-[calc(44px+var(--webstore-header-height))]' : 'top-0';
};

const getPanelLeftClasses = (isActive: boolean, isMulti: boolean) => {
  if (!isMulti) {
    return 'md:left-[unset]';
  }

  return isActive ? 'md:left-0 lg:left-[unset]' : 'md:left-[-100%] lg:left-[unset]';
};

const getPanelRightClasses = (isActive: boolean, isMulti: boolean, isSide: boolean) => {
  // A side panel is positioned inside the clip box on lg, not against the
  // viewport, so getPanelSideMotionClasses owns its desktop offset.
  if (isSide) {
    return isActive ? 'md:right-0' : 'md:right-[-100%]';
  }
  if (!isActive) {
    return isMulti ? 'md:right-[unset] lg:right-[-100%]' : 'md:right-[-100%] lg:right-[-100%]';
  }
  if (isMulti) {
    return 'md:right-[unset] lg:right-0';
  }

  return 'md:right-0 lg:right-0';
};

// Width of the fixed (non-side) panel on desktop: lg:w-[40%]. Tailwind needs
// this as a literal class string (see below), but getPanelSideClipClasses'
// clip box is anchored to this same 40% edge, so both must stay in sync.
const getPanelWidthClasses = (isActive: boolean, isAnimation: boolean, isMiddle: boolean, isSide: boolean) => {
  // middle-active width handled by panel.css
  if (isMiddle && isActive) {
    return '';
  }

  if (isSide) {
    return 'md:w-[380px] lg:w-[360px] 2xl:w-[440px]';
  }

  return isAnimation || isActive ? 'md:w-[380px] lg:w-[40%]' : 'md:w-0 lg:w-0';
};

// The clip box for secondary (side) panels on desktop: a fixed region running
// from the viewport's left edge to the fixed panel's left edge. Anything a side
// panel does to the right of that edge is masked, which is what lets it slide in
// from the right without ever being seen crossing the fixed panel.
//
// `contents` keeps this element out of the layout below lg, where side panels
// still position themselves against the viewport and slide in over the standard
// panel as before. It must stay pointer-events-none — at lg it covers the whole
// preview area, and the panel inside re-enables events for itself when open.
const getPanelSideClipClasses = () =>
  // lg:right-[40%] mirrors getPanelWidthClasses' lg:w-[40%] fixed-panel width above.
  'contents lg:block lg:fixed lg:top-[var(--webstore-header-height)] lg:bottom-0 lg:left-0 lg:right-[40%] lg:overflow-hidden lg:pointer-events-none';

// Desktop motion for secondary (side) panels. Anchored to the right edge of the
// clip box — which sits flush against the fixed panel — and slid in from behind
// it. Same 0.4s curve as every other panel, so it reads the same as the search
// panel; the only difference is that the fixed panel masks the entry.
//
// `translate` (not `transform`) is the property Tailwind v4 emits for
// translate-x-*, so that is what has to be named in the transition.
const getPanelSideMotionClasses = (isActive: boolean) =>
  cn(
    'lg:absolute lg:top-0 lg:bottom-0 lg:left-[unset] lg:right-0',
    'lg:[transition:translate_0.4s_cubic-bezier(0.5,0.05,0.1,1)]',
    isActive ? 'lg:translate-x-0 lg:pointer-events-auto' : 'lg:translate-x-full',
  );

const getPanelContentClasses = (
  isActive: boolean,
  isAnimation: boolean,
  isDragState: boolean,
  isMiddle: boolean,
  isSide: boolean,
  panelSize: PanelSize,
  fillHeight: boolean,
) => {
  const isFull = panelSize === PanelSize.Full;
  const isContent = panelSize === PanelSize.Content;
  const isMulti = panelSize === PanelSize.Multi;

  return cn(
    'fixed bottom-0 left-0 right-0 bg-white overflow-x-hidden rounded-t-[8px] md:rounded-none [transition:top_0.4s_cubic-bezier(0.5,0.05,0.1,1)]',
    // Column layout so the inner wrapper can claim the height left over by the
    // drag bar — see `fillHeight` on PanelProps.
    fillHeight && 'flex flex-col',
    isMiddle && 'cm-panel-content--middle',
    isAnimation || isActive ? 'opacity-100' : 'opacity-0',
    isDragState ? 'overflow-y-hidden' : 'overflow-y-auto',
    isMulti
      ? 'md:[transition:left_0.4s_cubic-bezier(0.5,0.05,0.1,1)] lg:[transition:right_0.4s_cubic-bezier(0.5,0.05,0.1,1)]'
      : 'md:[transition:right_0.4s_cubic-bezier(0.5,0.05,0.1,1)]',
    getPanelTopClasses(isActive, isContent, isFull),
    isActive ? 'md:top-[var(--webstore-header-height)]' : 'md:top-0',
    getPanelLeftClasses(isActive, isMulti),
    getPanelRightClasses(isActive, isMulti, isSide),
    getPanelWidthClasses(isActive, isAnimation, isMiddle, isSide),
    isSide && getPanelSideMotionClasses(isActive),
    // max-height
    isContent && (isActive ? 'max-h-[calc(100%-44px)]' : 'max-h-0'),
    isActive ? 'md:max-h-none' : isContent && 'md:max-h-[unset]',
  );
};

const getBarClasses = (isMiddle: boolean) =>
  cn(
    "pt-8 pb-16 cursor-grab after:content-[''] after:block after:mx-auto after:w-40 after:h-4 after:rounded-sm after:bg-gray-400 md:hidden",
    isMiddle && 'absolute left-0 right-0 z-[2] m-auto',
  );

const getCloseClasses = (isActive: boolean, isMiddle: boolean) =>
  cn(
    'hidden',
    isActive &&
      !isMiddle &&
      'md:block md:cursor-pointer md:absolute md:w-24 md:h-24 md:right-20 md:top-20 md:text-gray-800 md:z-[100]',
  );

const getInnerClasses = (
  isInStore: boolean,
  isMiddle: boolean,
  panelSize: PanelSize,
  fillHeight: boolean,
) => {
  const isContent = panelSize === PanelSize.Content;
  const isMulti = panelSize === PanelSize.Multi;

  let sizeClasses = '';
  if (fillHeight) {
    // `min-h-0` is required: a flex item defaults to min-height:auto, which
    // would let a tall child push this past the panel instead of scrolling
    // inside it.
    sizeClasses = 'flex-1 min-h-0';
  } else if (isMulti) {
    sizeClasses = 'h-[calc(100%-32px)] relative md:h-full';
  } else if (isContent) {
    sizeClasses = 'h-full max-h-[calc(100dvh-76px-var(--webstore-header-height))] overflow-y-auto md:max-h-[unset] md:overflow-y-visible';
  }

  return cn(isMiddle && 'cm-panel-inner', isInStore && 'pb-[34px] md:pb-20 lg:pb-0', sizeClasses);
};

interface PanelProps {
  isActive: boolean;
  children?: React.ReactNode;
  panelSize?: PanelSize;
  panelType?: PanelType;
  panelPosition?: PanelPosition;
  zIndex?: number;
  withOverlay?: boolean;
  hasCloseBtn?: boolean;
  /**
   * Hand the child a definite height instead of letting it size to its content.
   *
   * Opt-in because most panels are documents that scroll: the panel element
   * itself owns the scrolling and the content just flows. Embedded apps are the
   * exception — they lay themselves out against `h-full` and pin their own
   * footer, which silently collapses when no ancestor resolves to a height
   * (the booking tool's submit button ends up under the form instead of at the
   * bottom of the panel).
   */
  fillHeight?: boolean;
  testIdSubfix: string;
  closeClick: () => void;
}

export default function Panel({
  isActive,
  children,
  panelSize = PanelSize.Full,
  panelType = PanelType.Regular,
  panelPosition = PanelPosition.Regular,
  zIndex = 12,
  withOverlay,
  hasCloseBtn = false,
  fillHeight = false,
  testIdSubfix,
  closeClick,
}: Readonly<PanelProps>) {
  const { client } = useUserContext();

  const webstoreHeaderHeight = client === Clients.onLine ? 44 : 0;
  const middleScreenOffsetY = 12 + webstoreHeaderHeight;
  const fullScreenOffsetY = 44 + webstoreHeaderHeight;
  const offsetFullScreen = panelType === PanelType.MiddleScreen ? middleScreenOffsetY : fullScreenOffsetY;
  const offsetMidScreen = 200;
  const [offsetY, setOffsetY] = useState<number>(panelSize === PanelSize.Multi ? offsetMidScreen : offsetFullScreen);
  const [isDragState, setIsDragState] = useState<boolean>(false);
  const [isAnimation, setIsAnimation] = useState<boolean>(false);
  const viewSize = useViewSize();

  useEffect(() => {
    document.documentElement.style.overscrollBehavior = isDragState ? 'none' : '';
    return () => {
      document.documentElement.style.overscrollBehavior = '';
    };
  }, [isDragState]);

  useEffect(() => {
    if (isActive) {
      setIsAnimation(true);
      return;
    }
    const timer = setTimeout(() => {
      setIsAnimation(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [isActive]);

  const dragTouchStart = (e: React.TouchEvent) => {
    dragStart(e.touches[0].clientY);
  };

  const dragMouseStart = (e: React.MouseEvent) => {
    dragStart(e.clientY);
  };

  const dragTouch = (e: TouchEvent) => {
    drag(e.touches[0].clientY);
  };

  const dragMouse = (e: MouseEvent) => {
    drag(e.clientY);
  };

  const dragTouchEnd = (e: TouchEvent) => {
    dragEnd(e.changedTouches[0].clientY);
  };

  const dragMouseEnd = (e: MouseEvent) => {
    dragEnd(e.clientY);
  };

  const dragStart = (clientY: number) => {
    setIsDragState(true);
    setOffsetY(clientY);
    document.addEventListener('mouseup', dragMouseEnd);
    document.addEventListener('mousemove', dragMouse);
    document.addEventListener('touchend', dragTouchEnd);
    document.addEventListener('touchmove', dragTouch);
  };

  const drag = (clientY: number) => {
    let coorY = clientY;
    if (coorY < offsetFullScreen) {
      coorY = offsetFullScreen;
    }
    setOffsetY(coorY);
  };

  const dragEnd = (clientY: number) => {
    const coorY = clientY;
    document.removeEventListener('mouseup', dragMouseEnd);
    document.removeEventListener('mousemove', dragMouse);
    document.removeEventListener('touchend', dragTouchEnd);
    document.removeEventListener('touchmove', dragTouch);

    if (panelSize === PanelSize.Multi) {
      if (coorY > offsetMidScreen + 80 && coorY > offsetY) {
        closeClick();
        setOffsetY(offsetMidScreen);
      } else if (coorY < offsetMidScreen + 80 && coorY > offsetY) {
        setOffsetY(offsetMidScreen);
      } else if (coorY < offsetY) {
        setOffsetY(offsetFullScreen);
      }
    } else if (coorY > offsetY) {
      setOffsetY(offsetFullScreen);
      closeClick();
    }

    setIsDragState(false);
  };

  const setTop = (panelSize: PanelSize) => {
    return panelSize === PanelSize.Multi ? '100%' : '';
  };

  const styleOverlayTransparent = {
    background: 'transparent',
    zIndex: isActive ? zIndex : -1,
  };

  const styleOverlayDefault = { zIndex: isActive ? zIndex : -1 };

  const isInStore = client === Clients.inStore;
  const isMiddle = panelType === PanelType.MiddleScreen;
  const isSide = panelPosition === PanelPosition.Side;

  const panelContent = (
    <div
      className={getPanelContentClasses(isActive, isAnimation, isDragState, isMiddle, isSide, panelSize, fillHeight)}
      data-testid={PANEL_TEST_IDS.getContentId(testIdSubfix)}
      style={
        viewSize === ViewSize.Mobile
          ? {
              zIndex: isActive ? zIndex : 'unset',
              top: isActive && (panelSize !== PanelSize.Content || isDragState) ? offsetY + 'px' : setTop(panelSize),
              transition: isDragState
                ? 'none'
                : 'max-height 0.4s cubic-bezier(0.5, 0.05, 0.1, 1), top 0.4s cubic-bezier(0.5, 0.05, 0.1, 1)',
            }
          : { zIndex: zIndex }
      }
    >
      <div
        className={getBarClasses(isMiddle)}
        role="button"
        tabIndex={0}
        onMouseDown={dragMouseStart}
        onTouchStart={dragTouchStart}
        onKeyDown={e => {
          onEnter(e, () => closeClick());
        }}
      ></div>
      {hasCloseBtn && (
        <div
          className={getCloseClasses(isActive, isMiddle)}
          role="button"
          tabIndex={0}
          onClick={closeClick}
          onKeyDown={e => {
            onEnter(e, () => closeClick());
          }}
          data-testid={PANEL_TEST_IDS.CLOSE_BTN}
        >
          <i className="susu-icons md:text-2xl" translate="no">
            close
          </i>
        </div>
      )}
      <div className={getInnerClasses(isInStore, isMiddle, panelSize, fillHeight)}>{children}</div>
    </div>
  );

  return (
    <div
      className={cn({
        'cm-panel--active': isActive,
        'cm-panel--middle': isMiddle,
      })}
    >
      <div
        className={getOverlayClasses(isActive, withOverlay)}
        style={
          offsetY > offsetFullScreen && panelSize !== PanelSize.Full && viewSize === ViewSize.Mobile
            ? styleOverlayTransparent
            : styleOverlayDefault
        }
        onClick={closeClick}
        onKeyDown={e => {
          onEnter(e, () => {
            closeClick();
          });
        }}
      ></div>
      {isSide ? (
        <div className={getPanelSideClipClasses()} style={{ zIndex }}>
          {panelContent}
        </div>
      ) : (
        panelContent
      )}
    </div>
  );
}
